import * as React from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useMappedState } from "redux-react-hook";

import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  TabbedNavigation,
  TabOptions
} from "src/library/components/TabbedNavigation/TabbedNavigation";
import { PageProps } from "src/pages/Router/routes";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { formatPrice } from "src/core/prices/utils/price-helpers";
import { useInvoiceDetails } from "src/core/accounting/hooks/useInvoiceDetails";
import AccountingStatusBadge from "src/core/accounting/components/AccountingStatusBadge";
import RelatedOrdersTab from "src/core/accounting/components/RelatedOrdersTab";
import RelatedTransactionsTab from "src/core/accounting/components/RelatedTransactionsTab";
import PDFInvoiceTab from "src/core/accounting/components/PDFInvoiceTab";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";

interface InvoiceDetailsPageProps {
  invoiceId: number;
}

const InvoiceDetailsPage: React.ReactFCWithChildren<PageProps<InvoiceDetailsPageProps>> = props => {
  const { match } = props;
  const history = useHistory();
  const invoiceId = match?.params.invoiceId ? parseInt(match?.params.invoiceId) : undefined;

  // Use branch-scoped access enforcement when in masquerade mode (customer viewing as specific drop)
  const {
    invoice, loading, error
  } = useInvoiceDetails(invoiceId);

  // Calculate VAT (assuming vatAmount is available, otherwise calculate from total)
  const vatAmount = invoice?.vatAmount || 0;
  const subtotal = invoice ? invoice.totalAmount - vatAmount : 0;

  // Build tabs for TabbedNavigation
  const tabs: TabOptions[] = React.useMemo(() => {
    const displayTabs = [
      {
        title: "Related Orders",
        name: "related-order",
        content: () => (
          <RelatedOrdersTab invoiceId={invoice?.id || 0} />
        )
      },

      {
        title: "Transactions",
        name: "transactions",
        content: () => <RelatedTransactionsTab invoiceId={invoiceId} />
      }
    ];

    if (invoice?.pdfKey) {
      displayTabs.push({
        title: "PDF Invoice",
        name: "pdf-invoice",
        content: () => <PDFInvoiceTab pdfKey={invoice.pdfKey} />
      });
    }

    return displayTabs;
  }, [
    invoice?.id,
    invoice?.pdfKey,
    invoiceId
  ]);

  if (loading) {
    return (
      <Layout {...props}>
        <MainContentWrapper>
          <LoadingSpinner type="circular" />
        </MainContentWrapper>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout {...props}>
        <MainContentWrapper>
          <Text color="danger">
            {`Error loading invoice: ${error.message}`}
          </Text>
        </MainContentWrapper>
      </Layout>
    );
  }

  return (
    <Layout {...props}>
      <MainContentWrapper>
        {invoice && (
          <>
            {/* Header with back button */}
            <PageHeader>
              <BackButton onClick={() => history.goBack()}>
                <Icon
                  icon={Icons.CHEVRONLEFT}
                  size={16}
                />

                <Text
                  fontSize="h1"
                  weight="bold"
                >
                  {`Invoice #${invoice.invoiceNumber}`}
                </Text>
              </BackButton>

              <AccountingStatusBadge
                variant="invoice"
                status={invoice.status}
              />
            </PageHeader>

            {/* Summary Section */}
            <SummarySection>
              <SummaryLeft>
                <Text
                  fontSize="h2"
                  weight="bold"
                >
                  Summary
                </Text>

                <SummaryRow>
                  <SummaryLabel>
                    Invoice date
                  </SummaryLabel>

                  <SummaryValue>
                    {new Date(invoice.invoiceDate).toLocaleDateString("en-GB", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                      year: "numeric"
                    })}
                  </SummaryValue>
                </SummaryRow>
              </SummaryLeft>

              <SummaryRight>
                <SummaryRow>
                  <SummaryLabel>
                    Invoice number
                  </SummaryLabel>

                  <SummaryValue>
                    {invoice.invoiceNumber}
                  </SummaryValue>
                </SummaryRow>

                <SummaryRow>
                  <SummaryLabel>
                    Subtotal (Excl. VAT)
                  </SummaryLabel>

                  <SummaryValue>
                    {formatPrice(subtotal)}
                  </SummaryValue>
                </SummaryRow>

                <SummaryRow>
                  <SummaryLabel>
                    Vat
                  </SummaryLabel>

                  <SummaryValue>
                    {formatPrice(vatAmount)}
                  </SummaryValue>
                </SummaryRow>

                <SummaryRow>
                  <SummaryLabel>
                    <strong>
                      Invoice Total
                    </strong>
                  </SummaryLabel>

                  <SummaryValue>
                    <strong>
                      {formatPrice(invoice.totalAmount)}
                    </strong>
                  </SummaryValue>
                </SummaryRow>

                <SummaryRow>
                  <SummaryLabel>
                    <strong>
                      Outstanding Amount
                    </strong>
                  </SummaryLabel>

                  <SummaryValue>
                    <strong>
                      {formatPrice(invoice.outstandingAmount)}
                    </strong>
                  </SummaryValue>
                </SummaryRow>

              </SummaryRight>
            </SummarySection>

            {/* Tabs */}
            <TabbedNavigation
              tabs={tabs}
              defaultTab="pdf-invoice"
            />
          </>
        )}
      </MainContentWrapper>
    </Layout>
  );
};

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.margins.md};
  margin-bottom: ${({ theme }) => theme.spacing.margins.lg};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.margins.sm};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const SummarySection = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.margins.lg};
  margin-bottom: ${({ theme }) => theme.spacing.margins.xl};
  padding: ${({ theme }) => theme.spacing.padding.lg};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
`;

const SummaryLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.margins.md};
  flex: 1;
`;

const SummaryRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.margins.sm};
  flex: 1;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SummaryLabel = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.p};
  color: ${({ theme }) => theme.fonts.colors.secondary};
`;

const SummaryValue = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.p};
  color: ${({ theme }) => theme.fonts.colors.primary};
`;

const PayButton = styled(Button)`
  margin-top: ${({ theme }) => theme.spacing.margins.md};
  width: 100%;
`;

export default InvoiceDetailsPage;
